import {
  IProductConfigCancelPolicy,
  ProductConfigCancelPolicyCriteria,
  ProductConfigCancelPolicyMethod,
} from '~/api/services/product-config/index.type'
import { TInventoryRatePlanItemDate } from '~/api/services/rate-plan-api/index.type'
import { DateTime } from 'luxon'
import {
  CancelPolicyType,
  ICancelPolicy,
} from '~/api/converters/domestic/order/ProductSection/Property/index.type'
import { formatISODate } from '~/api/utils/datetime'
import { isTruthy } from '~/app/utils'

type StayType = {
  date: Date
  price: number
}

const Hour = 60 * 60000
const KST = 9 * Hour

const calculateCancelPolicy = (stay: StayType, policy: IProductConfigCancelPolicy) => {
  return policy.policies.map(p => {
    const ratio = p.criteria === ProductConfigCancelPolicyCriteria.TOTAL ? 100 : (p.ratio ?? 0)
    return {
      startDate: p.startOffset
        ? new Date(stay.date.getTime() + (p.startOffset * 1000 + KST))
        : null,
      endDate: new Date(stay.date.getTime() + (p.endOffset * 1000 + KST)),
      price: stay.price,
      ratio,
      fee: (stay.price * ratio) / 100,
      criteria: p.criteria,
    }
  })
}

const cancelPolicies = (
  policy: IProductConfigCancelPolicy,
  dates: TInventoryRatePlanItemDate[]
) => {
  const stays = dates.map(v => ({
    date: new Date(`${v.date}T00:00:00+09:00`),
    price: v.rate,
  }))

  return stays.map(v => {
    return {
      stay: new Date(
        policy.method === ProductConfigCancelPolicyMethod.BY_DAYS ? v.date : stays[0].date
      ),
      policies: calculateCancelPolicy(
        policy.method === ProductConfigCancelPolicyMethod.BY_DAYS ? v : stays[0],
        policy
      ),
    }
  })
}

export const cancelPolicyHelper = (
  currentDate: string,
  checkInDate: string,
  configCancelPolicies: IProductConfigCancelPolicy | null,
  ratePlanDates: TInventoryRatePlanItemDate[],
  price: number
): ICancelPolicy | null => {
  if (!configCancelPolicies) {
    return null
  }

  const currentDateTime = DateTime.fromISO(currentDate)
  const checkIn = DateTime.fromISO(checkInDate)
  const policies = cancelPolicies(configCancelPolicies, ratePlanDates)
  const policy = policies
    .map(
      v =>
        v.policies.filter(
          p => DateTime.fromJSDate(p.endDate).toMillis() > currentDateTime.toMillis()
        )[0]
    )
    .filter(isTruthy)

  /*
   * [FES-1145] 수수료 계산 - (총 취소수수료 금액 / 상품가) * 100
   * 연박 예약시 일자별 상품 가격이 다를경우 기존 수수료율 계산에 차이가 발생하여 수정함
   * */
  const ratio =
    policy && checkIn > currentDateTime
      ? Math.ceil(+((policy.reduce((a, b) => a + b.fee, 0) / price) * 100).toFixed(2) * 10) / 10
      : 100

  /*
   * 취소 수수료 기간 변경
   * 투숙일별 취소 정책 기간이 상이하여 중간 기간을 표현하고자 추가됨
   * startDate는 가장 큰 값, endDate는 가장 작은 값
   * 시작시간은 계산하지만 기간 표기시 사용안됨
   * */
  const endAt = policy.length
    ? DateTime.min(...policy.map(v => DateTime.fromJSDate(v.endDate))).toISO()
    : undefined
  return {
    endAt,
    type:
      ratio <= 0
        ? CancelPolicyType.FREE
        : ratio < 100
          ? CancelPolicyType.RATIO
          : CancelPolicyType.TOTAL,
    weight: ratio,
  }
}

const dateWithDayOfWeek = (date: string) => {
  return formatISODate(date, 'yyyy.MM.dd (ccc)')
}

const hourMinute = (date: string) => {
  return formatISODate(date, 'HH:mm')
}
export const fromISO = (date: string) => {
  return date.length > 10
    ? DateTime.fromISO(date).setLocale('ko-KR')
    : DateTime.fromFormat(date, 'yyyy-MM-dd').setLocale('ko-KR')
}

export const getTimeOptions = (checkInAt: string, checkOutAt: string, isDayUse: boolean) => {
  const checkIn = {
    title: '체크인',
    date: dateWithDayOfWeek(checkInAt),
    time: hourMinute(checkInAt),
  }
  const checkOut = {
    title: '체크아웃',
    date: dateWithDayOfWeek(checkOutAt),
    time: hourMinute(checkOutAt),
  }
  const dayUseTimeOption = {
    title: '체크인 / 체크아웃',
    date: dateWithDayOfWeek(checkInAt),
    time: `${hourMinute(checkInAt)} ~ ${hourMinute(checkOutAt)}`,
  }

  return isDayUse ? [dayUseTimeOption] : [checkIn, checkOut]
}

export function getUseTimeText(checkInAt: string, checkOutAt: string, isRent: boolean): string {
  const rentDiff = fromISO(checkOutAt)
    .diff(fromISO(checkInAt), ['days', 'hours', 'minutes'])
    .toObject()
  const dayDiff = fromISO(checkOutAt)
    .startOf('day')
    .diff(fromISO(checkInAt).startOf('day'), ['days'])
    .toObject()

  return isRent
    ? [`${rentDiff.hours}시간`, rentDiff.minutes ? `${rentDiff.minutes}분` : '']
        .filter(isTruthy)
        .join(' ')
    : `${dayDiff.days}박`
}

/**
 * @link https://yanolja.atlassian.net/browse/YAWEB-350
 * @link https://yanoljagroup.atlassian.net/wiki/spaces/platform/pages/1346577466
 * 야놀자 취소 예외 규정 > 10분이내 무료 취소 제외 업체 (호텔)
 * channelNo 300 은 메리어트 호텔로 기능 개발이 긴급히(P0) 진행되어 MVP로 각 도메인이 하드코딩 방식으로 우선 진행
 * 이후 신규 호텔체인 추가 확정 시 별도 API를 통해 '예외무료 취소 처리 구분값'을 받을 수 있는 방향으로 검토/진행 예정
 *
 * 2025-07-09 [YAFE-2908] 메리어트 호텔 체인(310, 320) 신규 추가됨
 */
export const getIsExceptionalCancelPolicy = (channelNo?: number): boolean => {
  return [300, 310, 320].some(no => no === channelNo)
}

export const getCapacityText = (capacity: number, maxCapacity: number) => {
  return [capacity, maxCapacity]
    .reduce<string[]>((acc, row, index) => {
      if (!!row) {
        acc.push(index === 0 ? `기준 ${row}명` : `최대 ${row}명`)
      }
      return acc
    }, [])
    .join(' / ')
}
